import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import { graphql, graphqlOperation } from "../modules/AmplifyServices";
import * as queries from "../graphql/queries";
import { execReadByPK, execReadBySortkey } from "../modules/DBService";
import { getUserFromCache } from "../user/UserCommon";
import { CSVLink } from "react-csv";
import { generatePayrollPDF } from "./PayrollSummaryPDF";
// prettier-ignore
import {
    IconButton, Grid, TextField, LinearProgress, CircularProgress, Button, Dialog, DialogContent, DialogTitle, Snackbar, ListItem,
    ListItemIcon,
    ListItemText,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import CloseIcon from "@mui/icons-material/Close";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { customLocale } from "../utils/CalendarFunctions/CalendarConfig";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import moment from "moment-timezone";
import {
    bookingByCompany,
    listBookingStatuss
} from "../queries/ListBookingsQueries";
import { getTimezoneIdByGeoLoc } from "../modules/TimeService";

const textFieldStyling = {
    "& .MuiFilledInput-root": {
        background: "white",
        border: "1px solid rgba(0,0,0,0.3)",
        borderRadius: "4px",
        width: "200px"
    },
    "& .MuiFilledInput-root:hover": {
        background: "white",
        border: "1px solid rgba(0,0,0,0.5)",
        borderRadius: "4px"
    },
    "& .MuiFilledInput-root.Mui-focused": {
        background: "white",
        border: "1px solid rgba(0,0,0,0.3)",
        borderRadius: "4px"
    }
};

const KNOWN_STATUSES = ["COMPLETED", "CANCELLED", "NOSHOW"];

const csvHeaders = [
    { label: "Provider first name", key: "provider_firstname" },
    { label: "Provider last name", key: "provider_lastname" },
    { label: "Provider email", key: "provider_email" },
    { label: "Service name", key: "service_name" },
    { label: "Service duration (mins)", key: "service_duration" },
    { label: "Booking date (YYYY-MM-DD)", key: "booking_date" },
    { label: "Booking completion (YYYY-MM-DD)", key: "booking_completion" },
    { label: "Status", key: "status" },
    { label: "Client name", key: "client_name" },
    { label: "Client email", key: "client_email" },
    { label: "Service location", key: "service_location" },
    { label: "Address", key: "address" },
    { label: "City", key: "service_location_city" },
    { label: "State", key: "service_location_state" },
    { label: "Country", key: "service_location_country" }
];

const ExportPayrollList = () => {
    const loggedInUser = getUserFromCache();
    const [exportDialog, setExportDialog] = useState();
    const [exportRows, setExportRows] = useState();
    const [pdfPayload, setPdfPayload] = useState();
    const [exportFormat, setExportFormat] = useState("both");
    const [exportDownloadReady, setExportDownloadReady] = useState(false);
    const [prepareExportData, setPrepareExportData] = useState(false);
    const [linearProgressValue, setLinearProgressValue] = useState(0);
    const [exportFromDate, setExportFromDate] = useState(new Date());
    const [exportToDate, setExportToDate] = useState(new Date());
    const [minToDate, setMinToDate] = useState(new Date());
    const [msgOpen, setMsgOpen] = useState(false);
    const [snackMsg, setSnackMsg] = useState("");

    const useCompletedBookings =
        moment(exportFromDate).format("YYYY-MM-DDT00:00:00Z") >
        "2023-10-03T23:59:59.820Z";

    async function handleExportData() {
        setPrepareExportData(true);

        const startdateTimeblockid = {
            between: [
                {
                    startdate: `${moment(exportFromDate).format(
                        "YYYY-MM-DDT00:00:00Z"
                    )}`
                },
                {
                    startdate: `${moment(exportToDate).format(
                        "YYYY-MM-DDT23:59:59Z"
                    )}`
                }
            ]
        };

        const bookingsForExport = await execReadBySortkey({
            opname: "bookingByCompany",
            op: bookingByCompany,
            id: {
                companyId: loggedInUser.companyId
            },
            skey: {
                startdateTimeblockid
            }
        });
        setLinearProgressValue(25);

        await prepareExportRows(
            bookingsForExport && bookingsForExport.items
                ? bookingsForExport.items
                : []
        );
    }

    async function prepareExportRows(bookings) {
        const listCompanyLocations = await execReadByPK({
            opname: "companyLocationByCompany",
            op: queries.companyLocationByCompany,
            id: {
                companyId: loggedInUser.companyId
            }
        });
        setLinearProgressValue(45);

        const completedAtByBookingId = {};
        if (useCompletedBookings) {
            const completedBookings =
                await getCompletedBookingsWithinDateRange();
            if (completedBookings) {
                // Merge bookings completed in the range that weren't caught
                // by the startdate query (booking started before the range
                // but was marked completed inside it).
                const seenBookingIds = new Set(bookings.map((b) => b.id));
                for (const cb of completedBookings) {
                    if (cb.bookingId && cb.createdAt) {
                        completedAtByBookingId[cb.bookingId] = cb.createdAt;
                    }
                    if (cb.booking && !seenBookingIds.has(cb.booking.id)) {
                        bookings.push(cb.booking);
                        seenBookingIds.add(cb.booking.id);
                    }
                }
            }
            // Fallback: for bookings marked COMPLETED but not captured by the
            // range query (e.g. completed outside the report window), look up
            // their BookingStatus records directly by bookingId.
            const missingIds = bookings
                .filter(
                    (b) =>
                        b.status === "COMPLETED" &&
                        !completedAtByBookingId[b.id]
                )
                .map((b) => b.id);
            if (missingIds.length) {
                const fallbackMap = await fetchCompletionsForBookings(
                    missingIds
                );
                Object.assign(completedAtByBookingId, fallbackMap);
            }
        }
        setLinearProgressValue(70);

        if (!bookings.length) {
            setLinearProgressValue(0);
            setPrepareExportData(false);
            setExportDownloadReady(false);
            setSnackMsg(
                "No data found within this date range, please adjust the dates and try again."
            );
            setMsgOpen(true);
            return;
        }

        const rows = [];
        const companyLocations = listCompanyLocations.items || [];

        for (const booking of bookings) {
            const locationId = getBookingLocationIdFromTimeBlockId(
                booking.timeblockid
            );
            const locationName =
                locationId === "customer-location"
                    ? "Client Address"
                    : getLocationNameFromId(locationId, companyLocations);

            const isVirtual = booking.location === "Virtual Appointment";
            const locationCity = isVirtual
                ? getLocationCityFromId(locationId, companyLocations)
                : getLocationCityFromAddress(booking.location);
            const locationState = isVirtual
                ? getLocationStateFromId(locationId, companyLocations)
                : getLocationStateFromAddress(booking.location);
            const locationCountry = isVirtual
                ? getLocationCountryFromId(locationId, companyLocations)
                : getLocationCountryFromAddress(booking.location);

            const rawStatus = booking.status;
            const displayStatus = KNOWN_STATUSES.includes(rawStatus)
                ? rawStatus
                : "NOT COMPLETED";

            const tz = booking.timezone || "UTC";
            const bookingDateIso = moment(booking.startdate).tz(tz).format();

            let bookingCompletion;
            if (completedAtByBookingId[booking.id]) {
                bookingCompletion = moment(completedAtByBookingId[booking.id])
                    .tz(tz)
                    .format();
            } else if (rawStatus === "COMPLETED") {
                bookingCompletion = bookingDateIso;
            } else {
                bookingCompletion = "NOT COMPLETED";
            }

            const clientUser = booking.client && booking.client.user;
            const clientName = clientUser
                ? `${clientUser.firstname || ""} ${
                      clientUser.lastname || ""
                  }`.trim()
                : "";
            const clientEmail = clientUser ? clientUser.emailaddress || "" : "";

            rows.push({
                provider_id: booking.provider.id,
                provider_firstname: booking.provider.firstname,
                provider_lastname: booking.provider.lastname,
                provider_email: booking.provider.emailaddress,
                service_name: booking.servicetype.name,
                service_duration: booking.servicetype.minutes,
                booking_date: bookingDateIso,
                booking_completion: bookingCompletion,
                status: displayStatus,
                client_name: clientName,
                client_email: clientEmail,
                service_location: locationName,
                address: booking.location || "",
                service_location_city: locationCity,
                service_location_state: locationState,
                service_location_country: locationCountry,
                // Fields for PDF aggregation (not in CSV):
                _minutes: booking.minutes,
                _rawStatus: rawStatus,
                _rating: booking.rating ? booking.rating.ratingstars : null,
                _timezone: tz
            });
        }

        rows.sort((a, b) => {
            const lastNameCmp = (a.provider_lastname || "").localeCompare(
                b.provider_lastname || ""
            );
            if (lastNameCmp !== 0) return lastNameCmp;
            return a.booking_date.localeCompare(b.booking_date);
        });

        const summary = buildSummary(rows);

        setExportRows(rows);
        setPdfPayload({
            rows,
            summary,
            companyName: loggedInUser?.company?.companyname || "",
            fromDate: exportFromDate,
            toDate: exportToDate
        });
        setExportDownloadReady(true);
        setLinearProgressValue(100);
    }

    function buildSummary(rows) {
        const providerIds = new Set();
        let totalSessions = 0;
        let completed = 0;
        let cancelled = 0;
        let noshows = 0;
        let completedMinutes = 0;
        let ratingSum = 0;
        let ratingCount = 0;

        for (const r of rows) {
            totalSessions += 1;
            providerIds.add(r.provider_id);
            if (r._rawStatus === "COMPLETED") {
                completed += 1;
                completedMinutes += r._minutes || 0;
            } else if (r._rawStatus === "CANCELLED") {
                cancelled += 1;
            } else if (r._rawStatus === "NOSHOW") {
                noshows += 1;
            }
            if (r._rating) {
                ratingSum += r._rating;
                ratingCount += 1;
            }
        }

        return {
            totalSessions,
            completed,
            cancelled,
            noshows,
            completedHours: +(completedMinutes / 60).toFixed(1),
            completionRate:
                totalSessions > 0
                    ? +((completed / totalSessions) * 100).toFixed(1)
                    : 0,
            activeProviders: providerIds.size,
            avgRating: ratingCount > 0 ? +(ratingSum / ratingCount).toFixed(1) : null,
            ratingCount
        };
    }

    function getBookingLocationIdFromTimeBlockId(timeBlockId) {
        if (!timeBlockId) return null;
        if (timeBlockId.includes("PL-")) {
            return "customer-location";
        }
        if (timeBlockId.includes("CL-")) {
            const blocks = timeBlockId.split("|");
            for (const block of blocks) {
                if (block.includes("CL-")) {
                    const locationId = block.split("CL-")[1].split("|")[0];
                    return locationId;
                }
            }
        }
        return null;
    }

    function getLocationNameFromId(locationId, locationsList) {
        const foundLocation = locationsList.find(
            (location) => location.id === locationId
        );
        return foundLocation ? foundLocation.locationname : "";
    }

    function getLocationCityFromId(locationId, locationsList) {
        const foundLocation = locationsList.find(
            (location) => location.id === locationId
        );
        return foundLocation ? foundLocation.city : "";
    }

    function getLocationStateFromId(locationId, locationsList) {
        const foundLocation = locationsList.find(
            (location) => location.id === locationId
        );
        return foundLocation ? foundLocation.state : "";
    }

    function getLocationCountryFromId(locationId, locationsList) {
        const foundLocation = locationsList.find(
            (location) => location.id === locationId
        );
        return foundLocation ? foundLocation.country : "";
    }

    function getLocationCityFromAddress(address) {
        if (!address) return "";
        const addressParts = address.split(",");
        const lastPart = addressParts[addressParts.length - 1].trim();
        const statePostalCode =
            addressParts[Math.max(0, addressParts.length - 2)].trim();
        const statePostalCodeParts = statePostalCode.split(" ");
        if (addressParts.length > 2) {
            if (
                lastPart === "Australia" ||
                lastPart === "AUS" ||
                lastPart === "United Kingdom" ||
                lastPart === "UK"
            ) {
                return statePostalCodeParts.slice(0, -2).join(" ");
            }
            return addressParts[Math.max(0, addressParts.length - 3)].trim();
        }
        return address;
    }

    function getLocationStateFromAddress(address) {
        if (!address) return "";
        const addressParts = address.split(",");
        const country = addressParts[addressParts.length - 1].trim();
        const statePostalCode =
            addressParts[Math.max(0, addressParts.length - 2)].trim();
        const statePostalCodeParts = statePostalCode.split(" ");
        if (country === "Australia" || country === "AUS") {
            return statePostalCodeParts[statePostalCodeParts.length - 2];
        } else if (country === "United Kingdom" || country === "UK") {
            return "";
        } else if (country === "Canada" || country === "CAN") {
            return statePostalCodeParts.slice(0, -2).join(" ");
        }
        return statePostalCodeParts.slice(0, -1).join(" ");
    }

    function getLocationCountryFromAddress(address) {
        if (!address) return "";
        const addressParts = address.split(",");
        return addressParts[addressParts.length - 1].trim();
    }

    function convertDateToMasterStr(dateToConvert, companyTz, timeStampType) {
        const localDateString =
            timeStampType === "START_OF_DAY"
                ? moment(dateToConvert).format("YYYY-MM-DDT00:00:00")
                : moment(dateToConvert).format("YYYY-MM-DDT23:59:59");
        const date = new Date(localDateString);
        const DateInTz = new Date(
            date.toLocaleString("en-US", { timeZone: companyTz })
        );
        const utcDate = new Date(
            date.toLocaleString("en-US", { timeZone: "UTC" })
        );
        const offset = utcDate.getTime() - DateInTz.getTime();
        const gmtDate = new Date(date.getTime() + offset);
        return timeStampType === "START_OF_DAY"
            ? moment(gmtDate).format("YYYY-MM-DDTHH:mm:ss.000")
            : moment(gmtDate).format("YYYY-MM-DDTHH:mm:ss.999");
    }

    async function getCompletedBookingsWithinDateRange() {
        try {
            const companyTz = await getTimezoneIdByGeoLoc({
                lat: loggedInUser.company.latitude,
                lng: loggedInUser.company.longitude
            });
            const fromDateStringInMasterTz = convertDateToMasterStr(
                exportFromDate,
                companyTz,
                "START_OF_DAY"
            );
            const toDateStringInMasterTz = convertDateToMasterStr(
                exportToDate,
                companyTz,
                "END_OF_DAY"
            );

            const allItems = [];
            let nextToken = null;
            do {
                const result = await graphql(
                    graphqlOperation(listBookingStatuss, {
                        companyId: loggedInUser.companyId,
                        limit: process.env.REACT_APP_LISTLIMIT,
                        status: {
                            between: [
                                `COMPLETED|${fromDateStringInMasterTz}`,
                                `COMPLETED|${toDateStringInMasterTz}`
                            ]
                        },
                        nextToken
                    })
                );
                if (result && result.data.listBookingStatuss) {
                    allItems.push(...result.data.listBookingStatuss.items);
                    nextToken = result.data.listBookingStatuss.nextToken || null;
                } else {
                    nextToken = null;
                }
            } while (nextToken);
            return allItems.length ? allItems : null;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    async function fetchCompletionsForBookings(bookingIds) {
        const map = {};
        const BATCH = 25;
        for (let i = 0; i < bookingIds.length; i += BATCH) {
            const chunk = bookingIds.slice(i, i + BATCH);
            const results = await Promise.all(
                chunk.map(async (bookingId) => {
                    try {
                        const res = await graphql(
                            graphqlOperation(queries.statusByBooking, {
                                bookingId,
                                status: { beginsWith: "COMPLETED" },
                                sortDirection: "DESC",
                                limit: 1
                            })
                        );
                        const items =
                            res &&
                            res.data &&
                            res.data.statusByBooking &&
                            res.data.statusByBooking.items;
                        if (items && items.length) {
                            return { bookingId, createdAt: items[0].createdAt };
                        }
                    } catch (e) {
                        console.log(
                            `statusByBooking failed for ${bookingId}`,
                            e
                        );
                    }
                    return null;
                })
            );
            for (const r of results) {
                if (r && r.createdAt) {
                    map[r.bookingId] = r.createdAt;
                }
            }
        }
        return map;
    }

    function handleExportClick() {
        setExportDialog(true);
    }

    function handleMsgClose() {
        setMsgOpen(false);
    }

    function resetExportState() {
        setExportDialog(false);
        setPrepareExportData(false);
        setExportDownloadReady(false);
        setMinToDate(new Date());
        setExportToDate(new Date());
        setExportFromDate(new Date());
        setLinearProgressValue(0);
        setExportRows();
        setPdfPayload();
        setExportFormat("both");
    }

    function downloadCsv() {
        if (!exportRows) return;
        const escape = (val) => {
            const s = val === null || val === undefined ? "" : String(val);
            return `"${s.replace(/"/g, '""')}"`;
        };
        const headerLine = csvHeaders.map((h) => escape(h.label)).join(",");
        const dataLines = exportRows.map((row) =>
            csvHeaders.map((h) => escape(row[h.key])).join(",")
        );
        const csvContent = [headerLine, ...dataLines].join("\r\n");
        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;"
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filenameBase}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    const filenameBase = `Provider data ${moment(exportFromDate).format(
        "YYYY-MM-DD"
    )} to ${moment(exportToDate).format("YYYY-MM-DD")}`;

    return (
        <>
            <Snackbar
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center"
                }}
                open={msgOpen}
                autoHideDuration={3000}
                onClose={handleMsgClose}
                ContentProps={{
                    "aria-describedby": "message-id"
                }}
                message={<span id="message-id">{snackMsg}</span>}
            />
            <Dialog
                open={exportDialog}
                fullWidth={true}
                onClose={resetExportState}
                PaperProps={{
                    sx: { width: "600px" }
                }}
                TransitionProps={{
                    onEnter: () => {}
                }}
            >
                <DialogTitle>
                    <Typography
                        variant="h4"
                        sx={{ fontSize: "24px", fontWeight: 400 }}
                    >
                        Export provider performance & payroll data
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={resetExportState}
                        style={{
                            position: "absolute",
                            right: "16px",
                            top: "8px",
                            color: "primary"
                        }}
                        size="large"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    dividers
                    style={{ padding: "18px 24px 18px 24px" }}
                >
                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={customLocale}
                    >
                        <Grid item>
                            <Typography
                                variant="body1"
                                gutterBottom
                                style={{ marginBottom: "6px" }}
                            >
                                {"Date range:"}
                            </Typography>
                            <Grid container sx={{ gap: "12px" }}>
                                <DesktopDatePicker
                                    value={exportFromDate}
                                    label="From"
                                    inputFormat="MM/dd/yyyy"
                                    disabled={
                                        !exportDownloadReady &&
                                        prepareExportData
                                    }
                                    InputProps={{ disableUnderline: true }}
                                    renderInput={(params) => (
                                        <TextField
                                            sx={textFieldStyling}
                                            variant="filled"
                                            {...params}
                                        />
                                    )}
                                    onChange={(newValue) => {
                                        setLinearProgressValue(0);
                                        setPrepareExportData(false);
                                        setExportDownloadReady(false);
                                        setMinToDate(newValue);
                                        setExportFromDate(newValue);
                                        if (newValue > exportToDate) {
                                            setExportToDate(newValue);
                                        }
                                    }}
                                />
                                <DesktopDatePicker
                                    minDate={exportFromDate}
                                    value={exportToDate}
                                    label="To"
                                    inputFormat="MM/dd/yyyy"
                                    disabled={
                                        !exportDownloadReady &&
                                        prepareExportData
                                    }
                                    InputProps={{ disableUnderline: true }}
                                    renderInput={(params) => (
                                        <TextField
                                            sx={textFieldStyling}
                                            variant="filled"
                                            {...params}
                                        />
                                    )}
                                    onChange={(newValue) => {
                                        setLinearProgressValue(0);
                                        setPrepareExportData(false);
                                        setExportDownloadReady(false);
                                        setExportToDate(newValue);
                                    }}
                                />
                            </Grid>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: "rgb(111 111 111 / 87%)",
                                    fontStyle: "italic",
                                    marginTop: "8px"
                                }}
                            >
                                Includes any booking that started in this
                                range, or was marked completed within it.
                            </Typography>
                        </Grid>
                    </LocalizationProvider>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={async () => {
                                setExportDownloadReady(false);
                                await handleExportData();
                            }}
                            disabled={prepareExportData && !exportDownloadReady}
                            style={{ marginTop: "18px" }}
                        >
                            Prepare Export
                        </Button>
                    </Grid>
                    {prepareExportData && !exportDownloadReady && (
                        <Grid
                            container
                            style={{
                                flexDirection: "column",
                                marginTop: "24px",
                                gap: "16px"
                            }}
                        >
                            <CircularProgress
                                color="primary"
                                size={24}
                                style={{ alignSelf: "center" }}
                            />
                            <LinearProgress
                                color="primary"
                                variant="buffer"
                                valueBuffer={linearProgressValue}
                                value={linearProgressValue}
                            />
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: "rgb(111 111 111 / 87%)",
                                    fontStyle: "italic",
                                    textAlign: "center"
                                }}
                            >
                                This may take a while...
                            </Typography>
                        </Grid>
                    )}
                    {exportDownloadReady && (
                        <>
                            <br></br>
                            <Grid item xs={12}>
                                <FormControl>
                                    <FormLabel
                                        sx={{ fontSize: "14px" }}
                                    >
                                        Format
                                    </FormLabel>
                                    <RadioGroup
                                        row
                                        value={exportFormat}
                                        onChange={(e) =>
                                            setExportFormat(e.target.value)
                                        }
                                    >
                                        <FormControlLabel
                                            value="both"
                                            control={<Radio />}
                                            label="Both (CSV + PDF)"
                                        />
                                        <FormControlLabel
                                            value="csv"
                                            control={<Radio />}
                                            label="CSV (all bookings)"
                                        />
                                        <FormControlLabel
                                            value="pdf"
                                            control={<Radio />}
                                            label="PDF (summary)"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} style={{ marginTop: "12px" }}>
                                <Typography variant="body1" gutterBottom>
                                    {`${exportRows.length} booking${
                                        exportRows.length === 1 ? "" : "s"
                                    } found. Click below to download.`}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                {exportFormat === "csv" && (
                                    <CSVLink
                                        data={exportRows}
                                        headers={csvHeaders}
                                        filename={`${filenameBase}.csv`}
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<GetAppIcon />}
                                        >
                                            Download CSV
                                        </Button>
                                    </CSVLink>
                                )}
                                {exportFormat === "pdf" && pdfPayload && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<GetAppIcon />}
                                        onClick={() =>
                                            generatePayrollPDF({
                                                ...pdfPayload,
                                                filename: `${filenameBase}.pdf`
                                            })
                                        }
                                    >
                                        Download PDF
                                    </Button>
                                )}
                                {exportFormat === "both" && pdfPayload && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<GetAppIcon />}
                                        onClick={() => {
                                            downloadCsv();
                                            generatePayrollPDF({
                                                ...pdfPayload,
                                                filename: `${filenameBase}.pdf`
                                            });
                                        }}
                                    >
                                        Download CSV + PDF
                                    </Button>
                                )}
                            </Grid>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <ListItem button onClick={() => handleExportClick()}>
                <ListItemIcon>
                    <GetAppIcon />
                </ListItemIcon>
                <ListItemText primary="Provider performance & payroll" />
            </ListItem>
        </>
    );
};

export default ExportPayrollList;
